import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import '@/components/raven/workspace.css';
import { Button } from '@repo/ui/button';
import { useEffect } from 'react';
import { Link, type LoaderFunctionArgs, Outlet, redirect, useLoaderData } from 'react-router';
import { GenericErrorLayout } from '@/components/generic-error';
import { getServerOrganizationSession } from '@/lib/auth-req';
import { useOrganizationStore } from '@/stores/organization-store';

const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const organizations = await getServerOrganizationSession(request);
  if (!organizations || organizations.length === 0 || !params.organizationId) {
    return redirect('/auth/signin');
  }
  const currentOrganization = organizations?.find((org) => org.id === params.organizationId);

  if (!currentOrganization) {
    return redirect('/auth/signin');
  }

  return { currentOrganization: currentOrganization, organizationId: params.organizationId };
};

export { loader as organizationIdLoader };

export const OrganizationIdLayout = () => {
  useLocale();

  const { setCurrentOrganization } = useOrganizationStore();

  const { currentOrganization, organizationId } = useLoaderData<typeof loader>();
  const orgNotFound = organizationId && !currentOrganization;

  useEffect(() => {
    if (currentOrganization) {
      setCurrentOrganization(currentOrganization);
    }
  }, [currentOrganization, setCurrentOrganization]);

  if (orgNotFound) {
    return (
      <GenericErrorLayout
        errorCode={404}
        errorCodeMap={{
          404: orgNotFound
            ? {
                get heading() {
                  return t('auth.organizationNotFound');
                },
                get subHeading() {
                  return t('auth.404OrganizationNotFound');
                },
                get message() {
                  return t('auth.theOrganizationYouAreLookingForMayHaveBeen');
                },
              }
            : {
                get heading() {
                  return t('auth.teamNotFound');
                },
                get subHeading() {
                  return t('auth.404TeamNotFound');
                },
                get message() {
                  return t('auth.theOrganizationYouAreLookingForMayHaveBeen');
                },
              },
        }}
        primaryButton={
          <Button asChild>
            <Link to="/">{t('auth.goHome')}</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="raven-workspace flex-1">
      <Outlet />
    </div>
  );
};
